import * as React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Fab from "@mui/material/Fab";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import { useGridApiRef } from "@mui/x-data-grid";
import { store } from "../../../../../store";
import { NoRowOverlay } from "../../../../table/NoRowOverlay";
import LocalizedDataGrid from "../../../../ui/datagrid/LocalizedDataGrid";
import CustomFilter from "../../../../table/CustomFilter";
import CSVDownloader from "../../../../ui/CSVDownloader";
import { getToday } from "../../../../ui/utils/DateUtils";
import {
  getSamples,
  getSamplesByCenter,
} from "../../../../../redux/actions/sample";

const SPACING = 50;

const createEmptyComboFilters = () => ({
  reference: "",
  centerReference: "",
  productionReference: "",
  patientCode: "",
  analyses: "",
  quantity: "",
  type: "",
  status: "",
});

const toOption = (value) => ({
  value,
  label: value,
});

const uniqueSortedOptions = (values) =>
  [...new Set(values.filter(Boolean).map((value) => String(value)))]
    .sort((a, b) => a.localeCompare(b))
    .map(toOption);

const formatSampleType = (type) => {
  if (!type) return "";
  return String(type).charAt(0).toUpperCase() + String(type).slice(1);
};

const getSampleTypeValue = (sample = {}) =>
  sample.sampleTypeDefinition?.name ||
  sample.SampleTypeDefinition?.name ||
  sample.sampleType?.name ||
  sample.SampleType?.name ||
  sample.sampleTypeName ||
  sample.type ||
  (typeof sample.sampleType === "string" ? sample.sampleType : "") ||
  "";

export const ScreenSamples = () => {
  const apiRef = useGridApiRef();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const samples = useSelector((state) => state.sample.samples);
  const isLoading = useSelector((state) => state.sample.isLoadingSamples);
  const studyId = location.state?.studyId;
  const studyCode = location.state?.studyCode;
  const centerNumber = location.state?.centerNumber;
  const centerId = location.state?.centerId;

  const routeState = React.useMemo(
    () => ({
      studyId,
      studyCode,
      centerNumber,
      centerId,
    }),
    [centerId, centerNumber, studyCode, studyId],
  );

  React.useEffect(() => {
    if (centerId) {
      store.dispatch(getSamplesByCenter(centerId));
      return;
    }

    store.dispatch(getSamples());
  }, [centerId]);

  const [comboFilters, setComboFilters] = React.useState(
    createEmptyComboFilters,
  );
  const [selectedRowIds, setSelectedRowIds] = React.useState([]);

  const rows = React.useMemo(
    () =>
      (samples || []).map((sample) => {
        const analysisNames = (sample.analyses || [])
          .map((analysis) => analysis.name)
          .filter(Boolean);
        const sampleTypeValue = getSampleTypeValue(sample);

        return {
          ...sample,
          type: sampleTypeValue,
          referenceFilter: sample.reference ? String(sample.reference) : "",
          centerReference:
            sample.center?.centerNumber ||
            sample.Center?.centerNumber ||
            sample.centerReference ||
            sample.centerNumber ||
            centerNumber ||
            "",
          productionReference:
            sample.production?.reference ||
            sample.Production?.reference ||
            sample.productionReference ||
            "",
          patientCodeFilter:
            sample.patientCode || sample.patientKey
              ? String(sample.patientCode || sample.patientKey)
              : "",
          analysesLabel: analysisNames.join(", "),
          analysisNames,
          quantityFilter:
            sample.quantity === null || sample.quantity === undefined
              ? ""
              : String(sample.quantity),
          typeFilter: sampleTypeValue ? String(sampleTypeValue) : "",
          statusFilter: sample.status ? String(sample.status) : "",
        };
      }),
    [centerNumber, samples],
  );

  const referenceOptions = React.useMemo(
    () => uniqueSortedOptions(rows.map((row) => row.referenceFilter)),
    [rows],
  );

  const patientCodeOptions = React.useMemo(
    () => uniqueSortedOptions(rows.map((row) => row.patientCodeFilter)),
    [rows],
  );

  const centerReferenceOptions = React.useMemo(
    () => uniqueSortedOptions(rows.map((row) => row.centerReference)),
    [rows],
  );

  const productionReferenceOptions = React.useMemo(
    () => uniqueSortedOptions(rows.map((row) => row.productionReference)),
    [rows],
  );

  const analysisOptions = React.useMemo(
    () => uniqueSortedOptions(rows.flatMap((row) => row.analysisNames)),
    [rows],
  );

  const quantityOptions = React.useMemo(
    () => uniqueSortedOptions(rows.map((row) => row.quantityFilter)),
    [rows],
  );

  const typeOptions = React.useMemo(
    () =>
      uniqueSortedOptions(rows.map((row) => row.typeFilter)).map((option) => ({
        ...option,
        label: t(option.value, formatSampleType(option.value)),
      })),
    [rows, t],
  );

  const statusOptions = React.useMemo(
    () => uniqueSortedOptions(rows.map((row) => row.statusFilter)),
    [rows],
  );

  const updateComboFilter = React.useCallback((field, value) => {
    setComboFilters((currentFilters) => ({
      ...currentFilters,
      [field]: value || "",
    }));
  }, []);

  const activeFilterLabels = React.useMemo(
    () =>
      [
        comboFilters.reference,
        comboFilters.centerReference,
        comboFilters.productionReference,
        comboFilters.patientCode,
        comboFilters.analyses,
        comboFilters.quantity,
        comboFilters.type,
        comboFilters.status,
      ].filter(Boolean),
    [comboFilters],
  );

  const handleResetFilters = React.useCallback(() => {
    setComboFilters(createEmptyComboFilters());
    apiRef.current?.setFilterModel?.({ items: [] });
  }, [apiRef]);

  const displayedSamples = React.useMemo(
    () =>
      rows.filter((sample) => {
        if (
          comboFilters.reference &&
          sample.referenceFilter !== comboFilters.reference
        ) {
          return false;
        }

        if (
          comboFilters.centerReference &&
          sample.centerReference !== comboFilters.centerReference
        ) {
          return false;
        }

        if (
          comboFilters.productionReference &&
          sample.productionReference !== comboFilters.productionReference
        ) {
          return false;
        }

        if (
          comboFilters.patientCode &&
          sample.patientCodeFilter !== comboFilters.patientCode
        ) {
          return false;
        }

        if (
          comboFilters.analyses &&
          !sample.analysisNames.includes(comboFilters.analyses)
        ) {
          return false;
        }

        if (
          comboFilters.quantity &&
          sample.quantityFilter !== comboFilters.quantity
        ) {
          return false;
        }

        if (comboFilters.type && sample.typeFilter !== comboFilters.type) {
          return false;
        }

        if (
          comboFilters.status &&
          sample.statusFilter !== comboFilters.status
        ) {
          return false;
        }

        return true;
      }),
    [comboFilters, rows],
  );

  const downloadSamples = React.useMemo(
    () =>
      displayedSamples
        .filter((sample) => selectedRowIds.includes(sample.id))
        .map(
          ({
            analysisNames,
            analysesLabel,
            patientCodeFilter,
            quantityFilter,
            referenceFilter,
            statusFilter,
            typeFilter,
            ...sample
          }) => sample,
        ),
    [displayedSamples, selectedRowIds],
  );

  React.useEffect(() => {
    const visibleIds = new Set(displayedSamples.map((sample) => sample.id));
    setSelectedRowIds((currentIds) =>
      currentIds.filter((rowId) => visibleIds.has(rowId)),
    );
  }, [displayedSamples]);

  const columns = React.useMemo(() => {
    const comboFilterOperator = (field, label, options, matchesRow) => [
      {
        label,
        value: field,
        getApplyFilterFn: (filterItem) => {
          if (!filterItem.value) return null;
          return ({ row }) => matchesRow(row, filterItem.value);
        },
        InputComponent: (props) => (
          <CustomFilter
            label={label}
            options={options}
            {...props}
            item={{
              ...props.item,
              value: props.item.value || comboFilters[field],
            }}
            applyValue={(nextItem) => {
              props.applyValue(nextItem);
              updateComboFilter(field, nextItem.value);
            }}
          />
        ),
      },
    ];

    return [
      {
        field: "actions",
        headerName: "",
        sortable: false,
        filterable: false,
        width: 70,
        renderCell: (params) => (
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              navigate(`/samples/${params.row.id}`, { state: routeState });
            }}>
            <EditIcon fontSize="small" />
          </IconButton>
        ),
      },
      {
        field: "reference",
        headerName: t("reference", "Reference"),
        flex: 0.2,
        minWidth: 160,
        filterable: true,
        filterOperators: comboFilterOperator(
          "reference",
          t("reference", "Reference"),
          referenceOptions,
          (row, value) => row.referenceFilter === value,
        ),
        renderCell: (params) => params.value || "-",
      },
      {
        field: "centerReference",
        headerName: t("centerReference", "Center reference"),
        flex: 0.2,
        minWidth: 170,
        filterable: true,
        filterOperators: comboFilterOperator(
          "centerReference",
          t("centerReference", "Center reference"),
          centerReferenceOptions,
          (row, value) => row.centerReference === value,
        ),
        renderCell: (params) => params.value || "-",
      },
      {
        field: "productionReference",
        headerName: t("productionReference", "Production ref"),
        flex: 0.2,
        minWidth: 170,
        filterable: true,
        filterOperators: comboFilterOperator(
          "productionReference",
          t("productionReference", "Production ref"),
          productionReferenceOptions,
          (row, value) => row.productionReference === value,
        ),
        renderCell: (params) => params.value || "-",
      },
      {
        field: "patientCode",
        headerName: t("patientCode", "Patient Code"),
        flex: 0.2,
        minWidth: 180,
        filterable: true,
        filterOperators: comboFilterOperator(
          "patientCode",
          t("patientCode", "Patient Code"),
          patientCodeOptions,
          (row, value) => row.patientCodeFilter === value,
        ),
      },
      {
        field: "analysesLabel",
        headerName: t("analyses", "Analyses"),
        flex: 0.5,
        minWidth: 260,
        filterable: true,
        filterOperators: comboFilterOperator(
          "analyses",
          t("analyses", "Analyses"),
          analysisOptions,
          (row, value) => row.analysisNames.includes(value),
        ),
        renderCell: (params) => params.value || "-",
      },
      {
        field: "quantity",
        headerName: t("quantity", "Quantity"),
        flex: 0.15,
        minWidth: 120,
        filterable: true,
        filterOperators: comboFilterOperator(
          "quantity",
          t("quantity", "Quantity"),
          quantityOptions,
          (row, value) => row.quantityFilter === value,
        ),
      },
      {
        field: "type",
        headerName: t("type", "Type"),
        flex: 0.15,
        minWidth: 130,
        filterable: true,
        filterOperators: comboFilterOperator(
          "type",
          t("type", "Type"),
          typeOptions,
          (row, value) => row.typeFilter === value,
        ),
        renderCell: (params) =>
          params.value ? t(params.value, formatSampleType(params.value)) : "-",
      },
      {
        field: "status",
        headerName: t("status", "Status"),
        flex: 0.15,
        minWidth: 130,
        filterable: true,
        filterOperators: comboFilterOperator(
          "status",
          t("status", "Status"),
          statusOptions,
          (row, value) => row.statusFilter === value,
        ),
        renderCell: (params) => params.value || "-",
      },
    ];
  }, [
    analysisOptions,
    centerReferenceOptions,
    comboFilters,
    navigate,
    patientCodeOptions,
    productionReferenceOptions,
    quantityOptions,
    referenceOptions,
    routeState,
    statusOptions,
    t,
    typeOptions,
    updateComboFilter,
  ]);

  return (
    <Grid item xs={12}>
      <Box
        sx={{ position: "relative", pb: downloadSamples.length > 0 ? 12 : 0 }}>
        <Paper sx={{ display: "flex", flexDirection: "column" }}>
          <Box sx={{ width: "100%", height: "80vh", position: "relative" }}>
            <LocalizedDataGrid
              apiRef={apiRef}
              autoWidth
              slots={{
                noRowsOverlay: NoRowOverlay,
                loadingOverlay: LinearProgress,
              }}
              rows={displayedSamples}
              columns={columns}
              loading={isLoading}
              checkboxSelection
              disableRowSelectionOnClick
              onRowSelectionModelChange={(newSelection) => {
                setSelectedRowIds(newSelection);
              }}
              onRowDoubleClick={(params) => {
                navigate(`/samples/${params.row.id}`, { state: routeState });
              }}
              initialState={{
                pagination: {
                  paginationModel: { page: 0, pageSize: 50 },
                },
              }}
              pageSizeOptions={[20, 50, 100]}
              rowHeight={40}
            />
            {activeFilterLabels.length > 0 && (
              <Box
                sx={{
                  position: "absolute",
                  left: 16,
                  bottom: 10,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  maxWidth: "55%",
                  color: "text.secondary",
                  fontSize: "0.875rem",
                  zIndex: 2,
                }}>
                <IconButton
                  aria-label="Reset filters"
                  onClick={handleResetFilters}
                  size="small"
                  sx={{ p: 0.25 }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
                <Box
                  component="span"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}>
                  Filters: {activeFilterLabels.join(", ")}
                </Box>
              </Box>
            )}
          </Box>
        </Paper>
        {downloadSamples.length > 0 && (
          <Box sx={{ position: "absolute", left: 0, bottom: 16, zIndex: 3 }}>
            <CSVDownloader
              data={downloadSamples}
              filename={`${studyCode}${centerNumber ? `_${centerNumber}` : ""}_samples_${getToday()}`}
              name={t("samples", "Samples")}
            />
          </Box>
        )}
      </Box>
      <Fab
        color="primary"
        aria-label="add"
        onClick={() => navigate("/samples/new", { state: routeState })}
        style={{ position: "absolute", right: SPACING, bottom: SPACING }}>
        <AddIcon />
      </Fab>
    </Grid>
  );
};
